import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

export interface PaymentTerm {
  installments: number;
  order: number;
  frequency: 'semanal' | 'quincenal' | 'mensual' | 'dias_continuos';
  frequencyDays?: number;
}

@Component({
  selector: 'app-payment-terms-builder',
  templateUrl: './payment-terms-builder.component.html',
  styleUrls: ['./payment-terms-builder.component.css']
})
export class PaymentTermsBuilderComponent implements OnInit {
  @Input() paymentTerms: PaymentTerm[] = [];
  @Output() paymentTermsChange = new EventEmitter<PaymentTerm[]>();
  @Output() validityChange = new EventEmitter<boolean>();

  showModal = false;
  termForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.termForm = this.fb.group({
      installments: [1, [Validators.required, Validators.min(1), Validators.max(100)]],
      order: [0, [Validators.required, Validators.min(0), Validators.max(999)]],
      frequency: ['mensual', Validators.required],
      frequencyDays: [0]
    });
  }

  ngOnInit(): void {
    this.setupFrequencyValidation();
    this.emitValidity();
  }

  private setupFrequencyValidation(): void {
    this.termForm.get('frequency')?.valueChanges.subscribe(freq => {
      const daysControl = this.termForm.get('frequencyDays');
      if (freq === 'dias_continuos') {
        daysControl?.setValidators([Validators.required, Validators.min(1), Validators.max(300)]);
        if (daysControl?.value === 0) {
          daysControl.setValue(10); // Default value
        }
      } else {
        daysControl?.clearValidators();
        daysControl?.setValue(0);
      }
      daysControl?.updateValueAndValidity();
    });
  }

  openAddModal(): void {
    this.termForm.reset({
      installments: 1,
      order: this.paymentTerms.length,
      frequency: 'mensual',
      frequencyDays: 0
    });
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
    this.termForm.reset();
  }

  saveTerm(): void {
    if (this.termForm.invalid) return;

    const formValue = this.termForm.value;
    const newTerm: PaymentTerm = {
      installments: formValue.installments,
      order: formValue.order,
      frequency: formValue.frequency,
      frequencyDays: formValue.frequency === 'dias_continuos' ? formValue.frequencyDays : undefined
    };

    // Check for duplicates (same installments)
    const duplicateIndex = this.paymentTerms.findIndex(t => 
      t.installments === newTerm.installments
    );

    if (duplicateIndex !== -1) {
      alert('Ya existe una opción con este número de cuotas.');
      return;
    }

    // Solo agregar (no editar)
    this.paymentTerms.push(newTerm);

    this.sortTerms();
    this.emitChanges();
    this.closeModal();
  }

  deleteTerm(index: number): void {
    if (confirm('¿Estás seguro de eliminar esta opción?')) {
      this.paymentTerms.splice(index, 1);
      this.emitChanges();
    }
  }

  private sortTerms(): void {
    this.paymentTerms.sort((a, b) => a.order - b.order);
  }

  private emitChanges(): void {
    this.paymentTermsChange.emit(this.paymentTerms);
    this.emitValidity();
  }

  private emitValidity(): void {
    this.validityChange.emit(this.paymentTerms.length > 0);
  }

  getFrequencyLabel(term: PaymentTerm): string {
    switch (term.frequency) {
      case 'semanal': return 'Semanal';
      case 'quincenal': return 'Quincenal';
      case 'mensual': return 'Mensual';
      case 'dias_continuos': return `Cada ${term.frequencyDays} días`;
      default: return term.frequency;
    }
  }
}
